import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import TextInput from '~components/FormFields/v2/TextInput';
import { PrevailingWageBenefit } from '~hooks/usePrevailingWageBenefits/models';

import {
  BenefitDto,
  BenefitDtoAsyncError,
  benefitSchema,
  setBenefitDefaultValues,
} from './benefitSchema';

export type BenefitFormHandler = {
  onSubmit: () => Promise<BenefitDto | undefined>;
};

interface BenefitFormProps {
  asyncErrors?: BenefitDtoAsyncError[];
  benefit?: PrevailingWageBenefit;
}

const BenefitForm = forwardRef<BenefitFormHandler, BenefitFormProps>(function BenefitForm(
  { asyncErrors, benefit },
  ref,
) {
  const { control, handleSubmit, setError, clearErrors } = useForm<BenefitDto>({
    resolver: yupResolver(benefitSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setBenefitDefaultValues(benefit),
  });

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => resolve(data),
            () => resolve(undefined),
          )();
        });
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    if (asyncErrors) {
      for (const error of asyncErrors) {
        setError(error.field as keyof BenefitDto, { message: error.message });
      }
    }

    return () => {
      clearErrors();
    };
  }, [asyncErrors, clearErrors, setError]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.name')}
          name="name"
        />
      </Grid>
    </Grid>
  );
});

export default BenefitForm;
